/**
 * Compare surfaces — the Settings section (spec §4.2, §12.6), one subsection
 * per ticked result:
 *
 *  - **Cut & fill:** grid size (m).
 *  - **Deviation map:** the distance measured (`DeviationModeField`),
 *    tolerance ± (shown in mm, stored in m; disabled in horizontal mode, which
 *    computes no tolerance statistics) and the colour range From / To (m).
 *
 * Every field has a `FormMessage`; the dialog's `FormMessageText` turns the
 * schema's tags into text.
 */
import { useId, type ReactNode } from 'react';
import { useWatch, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { LayerData } from '@/atoms/layers.atom';
import { AnalysisSection } from '@/components/analyses/sections';
import { DeviationModeField } from '@/components/compare/DeviationModeField';
import { SrUnit, UnitSuffix } from '@/components/compare/UnitSuffix';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { NumericField } from '@/components/ui/numeric-field';
import type { CompareFormValues } from '@/lib/analyses/compare/formSchema';

/** Metres → millimetres for display, without float dust (0.01 → 10). */
const toMm = (m: number) => Math.round(m * 1e6) / 1e3;

function SettingsGroup({ title, testId, children }: { title: ReactNode; testId: string; children: ReactNode }) {
  return (
    <fieldset data-testid={testId} className='flex min-w-0 flex-col gap-3'>
      <legend className='mb-2 text-sm font-semibold'>{title}</legend>
      {children}
    </fieldset>
  );
}

export interface CompareSettingsProps {
  form: UseFormReturn<CompareFormValues>;
  cutFillOn: boolean;
  deviationOn: boolean;
  design: LayerData | undefined;
}

export function CompareSettings({ form, cutFillOn, deviationOn, design }: CompareSettingsProps) {
  const { t } = useTranslation('project');
  const rangeLabelId = useId();
  const deviationMode = useWatch({ control: form.control, name: 'deviation.mode' });
  const horizontal = deviationMode === 'horizontal';

  if (!cutFillOn && !deviationOn) return null;

  return (
    <AnalysisSection title={t('analysisConfig.sections.settings')} testId='compare-section-settings'>
      {cutFillOn && (
        <SettingsGroup title={t('compareSurfaces.part.cutFill')} testId='compare-settings-cutFill'>
          <FormField
            control={form.control}
            name='cutFill.resolution'
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('compareSurfaces.settings.grid')}
                  <SrUnit unit='m' />
                </FormLabel>
                <UnitSuffix unit='m' testId='compare-grid-unit'>
                  <FormControl>
                    <NumericField
                      data-testid='compare-grid'
                      value={field.value}
                      onChange={field.onChange}
                      step='0.05'
                      min='0.01'
                      className='pr-10'
                    />
                  </FormControl>
                </UnitSuffix>
                <FormDescription className='text-xs'>{t('surfaceDifference.resolutionHelp')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </SettingsGroup>
      )}

      {deviationOn && (
        <SettingsGroup title={t('compareSurfaces.part.deviation')} testId='compare-settings-deviation'>
          <DeviationModeField
            value={deviationMode}
            onChange={(m) => form.setValue('deviation.mode', m, { shouldDirty: true })}
            design={design}
          />
          <FormField
            control={form.control}
            name='deviation.volumeTolerance'
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('compareSurfaces.settings.tolerance')}
                  <SrUnit unit='mm' />
                </FormLabel>
                <UnitSuffix unit='mm' testId='compare-tolerance-unit'>
                  <FormControl>
                    {/* Shown in mm, stored in m (spec §12.6). */}
                    <NumericField
                      data-testid='compare-tolerance'
                      value={toMm(field.value)}
                      onChange={(mm) => field.onChange(mm / 1000)}
                      step='1'
                      min='0'
                      disabled={horizontal}
                      className='pr-12'
                    />
                  </FormControl>
                </UnitSuffix>
                {horizontal ? (
                  // Horizontal mode computes no tolerance statistics (they
                  // live in the volume pass it turns off).
                  <FormDescription
                    data-testid='compare-tolerance-reason'
                    className='text-xs text-amber-700 dark:text-amber-400'>
                    {t('compareSurfaces.settings.toleranceHorizontal')}
                  </FormDescription>
                ) : (
                  <FormDescription className='text-xs'>{t('compareSurfaces.settings.toleranceHelp')}</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
          <div role='group' aria-labelledby={rangeLabelId} className='flex min-w-0 flex-col gap-2'>
            <span id={rangeLabelId} className='text-sm font-medium'>
              {t('compareSurfaces.settings.range')}
            </span>
            <div className='grid grid-cols-2 gap-3'>
              {(['min', 'max'] as const).map((end) => (
                <FormField
                  key={end}
                  control={form.control}
                  name={`deviation.${end}`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='font-normal'>
                        {end === 'min'
                          ? t('compareSurfaces.settings.rangeMin')
                          : t('compareSurfaces.settings.rangeMax')}
                        <SrUnit unit='m' />
                      </FormLabel>
                      <UnitSuffix unit='m' testId={`compare-range-${end}-unit`}>
                        <FormControl>
                          <NumericField
                            data-testid={`compare-range-${end}`}
                            value={field.value}
                            onChange={field.onChange}
                            step='0.001'
                            className='pr-10'
                          />
                        </FormControl>
                      </UnitSuffix>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <p className='text-xs text-muted-foreground'>{t('deviationAnalysis.rangeIsDisplayOnly')}</p>
          </div>
        </SettingsGroup>
      )}
    </AnalysisSection>
  );
}
